import { useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Angry,
  Bug,
  CheckCircle2,
  Frown,
  HelpCircle,
  Laugh,
  Lightbulb,
  Meh,
  MessageSquare,
  MessageSquareText,
  Smile,
  X,
} from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { btn } from "../Button/buttonStyles";
import { FIELD_BASE, FIELD_LABEL, fieldState } from "../Form/formFieldStyles";

const MAX_LENGTH = 2000;

const RATINGS = [
  { value: 1, label: "Very bad", icon: Angry },
  { value: 2, label: "Bad", icon: Frown },
  { value: 3, label: "Okay", icon: Meh },
  { value: 4, label: "Good", icon: Smile },
  { value: 5, label: "Great", icon: Laugh },
];

const CATEGORIES = [
  { value: "bug", label: "Bug", icon: Bug },
  { value: "idea", label: "Idea", icon: Lightbulb },
  { value: "question", label: "Question", icon: HelpCircle },
  { value: "other", label: "Other", icon: MessageSquare },
];

const EMPTY = { rating: null, category: "idea", message: "", canContact: true };

// Floating feedback button for the dashboard. It sits bottom-right, clear of
// the top-right toasts, and opens a small panel above itself. A draft survives
// closing the panel; it only clears once sent.
const FeedbackWidget = () => {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (e) => {
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && !sent) textareaRef.current?.focus();
  }, [open, sent]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;

    setSubmitting(true);
    const payload = {
      ...form,
      message: form.message.trim(),
      page: window.location.pathname,
      submitted_at: new Date().toISOString(),
    };
    // TODO: send `payload` once the feedback destination is decided.
    console.info("Feedback (not sent anywhere yet):", payload);
    setSubmitting(false);
    setSent(true);
    setForm(EMPTY);
  };

  const close = () => {
    setOpen(false);
    // Reopening after a send starts on a fresh form rather than the thank-you.
    if (sent) setTimeout(() => setSent(false), 200);
  };

  const remaining = MAX_LENGTH - form.message.length;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-label="Share feedback"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className="fixed bottom-20 right-4 sm:right-6 z-40 w-[calc(100vw-2rem)] max-w-[380px] origin-bottom-right rounded-2xl border border-gray-200/80 bg-white shadow-pop"
          >
            <header className="flex items-start justify-between gap-3 px-5 pt-5">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold tracking-tight text-gray-900">
                  Share feedback
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  We are just getting started. Tell us what works and what does not.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close feedback"
                className="-mr-1 -mt-1 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            {sent ? (
              <div className="px-5 pb-6 pt-6 text-center">
                <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-success-100 text-success-800">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <p className="mt-3 text-sm font-semibold text-gray-900">Thanks for the feedback!</p>
                <p className="mt-1 text-xs text-gray-500">
                  Every note is read and helps decide what we build next.
                </p>
                <div className="mt-5 flex justify-center gap-2">
                  <button type="button" onClick={() => setSent(false)} className={btn("secondary", "sm")}>
                    Send more
                  </button>
                  <button type="button" onClick={close} className={btn("primary", "sm")}>
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-4 px-5 pb-5 pt-4">
                <div>
                  <span className={FIELD_LABEL}>How is your experience so far?</span>
                  <div className="grid grid-cols-5 gap-1.5">
                    {RATINGS.map(({ value, label, icon: Icon }) => {
                      const on = form.rating === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => update({ rating: on ? null : value })}
                          aria-pressed={on}
                          title={label}
                          className={`flex flex-col items-center gap-1 rounded-lg border py-2 text-[10px] font-medium transition-colors ${
                            on
                              ? "border-brand-300 bg-brand-50 text-brand-700"
                              : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <Icon className="h-5 w-5" strokeWidth={1.75} />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <span className={FIELD_LABEL}>What is it about?</span>
                  <div className="flex flex-wrap gap-1.5">
                    {CATEGORIES.map(({ value, label, icon: Icon }) => {
                      const on = form.category === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => update({ category: value })}
                          aria-pressed={on}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            on
                              ? "border-brand-300 bg-brand-50 text-brand-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-message" className={FIELD_LABEL}>
                    Your feedback <span className="text-danger-500">*</span>
                  </label>
                  <textarea
                    ref={textareaRef}
                    id="feedback-message"
                    rows={5}
                    maxLength={MAX_LENGTH}
                    value={form.message}
                    onChange={(e) => update({ message: e.target.value })}
                    placeholder={
                      form.category === "bug"
                        ? "What happened, and what did you expect instead?"
                        : "Tell us what you think..."
                    }
                    className={`${FIELD_BASE} resize-none ${fieldState(false)} text-gray-900`}
                  />
                  <p className={`mt-1 text-right text-[11px] ${remaining < 100 ? "text-warning-700" : "text-gray-400"}`}>
                    {remaining} characters left
                  </p>
                </div>

                {user?.email && (
                  <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={form.canContact}
                      onChange={(e) => update({ canContact: e.target.checked })}
                      className="mt-0.5 h-4 w-4 cursor-pointer rounded accent-brand-600"
                    />
                    <span>
                      <span className="block font-medium text-gray-800">
                        Contact me about this feedback
                      </span>
                      <span className="block text-[11px] text-gray-500">
                        We may reply at {user.email}
                      </span>
                    </span>
                  </label>
                )}

                <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
                  <button type="button" onClick={close} className={btn("secondary", "sm")}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!form.message.trim() || submitting}
                    className={btn("primary", "sm")}
                  >
                    {submitting ? "Sending..." : "Send feedback"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        ref={buttonRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        className="fixed bottom-6 right-4 sm:right-6 z-40 inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-[13px] font-semibold text-white shadow-pop transition hover:bg-brand-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-200"
      >
        {open ? <X className="h-4 w-4" /> : <MessageSquareText className="h-4 w-4" />}
        Feedback
      </button>
    </>
  );
};

export default FeedbackWidget;
